import React from "react";
import { HiOutlineClipboardDocumentCheck, HiOutlineDocumentDuplicate } from "react-icons/hi2";
import Button from "components/ui/Button";
import { showToast } from "components/ui/toast";
import { blackButton, grayButton } from "./buttonLooks";

// The agent app's ConfirmChange card: what the change will do, then Confirm / Cancel, then how it ended. Unlike the
// agent's, every line is shown: the agent's card drops some, with nothing to open them again.

// As the agent's card reads once tapped: a change being made or made says Confirmed, one that was not made Cancelled.
const STATUS_LINE = {
  making: "Confirmed",
  made: "Confirmed",
  cancelling: "Cancelled",
  cancelled: "Cancelled",
  failed: "Cancelled",
};

// A point this long is words to use elsewhere (a caption, a message): it has its own Copy.
const LEAST_COPIED = 80;

const copyText = async (words, what) => {
  try {
    await navigator.clipboard.writeText(words);
    showToast("success", `${what} copied`);
  } catch {
    showToast("error", `Couldn't copy the ${what.toLowerCase()}.`);
  }
};

function Points({ points }) {
  if (!points.length) return null;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 border-t border-lightgray px-3.5 py-3 text-sm">
      {points.map((point, index) => (
        <React.Fragment key={`${point.label}-${index}`}>
          <dt className="text-xs font-semibold leading-5 text-darkgray">{point.label}</dt>
          <dd className="whitespace-pre-wrap break-words text-black">
            {point.text}
            {String(point.text).length >= LEAST_COPIED && (
              <button
                type="button"
                aria-label={`Copy ${point.label}`}
                onClick={() => copyText(String(point.text), point.label)}
                className="ml-1.5 inline-flex align-middle text-darkgray hover:text-black"
              >
                <HiOutlineDocumentDuplicate className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

// A change Pilot wants to make, waiting for the admin's Confirm or Cancel. `answering` is "confirm" or "cancel" while
// that answer is on its way.
export default function PilotChangeCard({ card, disabled, answering, onAnswer }) {
  const isDisabled = disabled || !!answering;
  const status = answering === "cancel" ? "cancelling" : answering ? "making" : card.status;
  return (
    <div className="overflow-hidden rounded-2xl border border-mediumgray bg-white">
      <div className="flex items-start gap-2.5 bg-lightgray px-3.5 py-3">
        <HiOutlineClipboardDocumentCheck className="mt-0.5 h-5 w-5 flex-none text-darkergray" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-black">{card.title}</p>
        </div>
      </div>

      <Points points={(card.lines || []).map((line) => ({ label: line.label.split(" › ").pop(), text: line.value }))} />

      <div className="border-t border-lightgray px-3.5 py-3">
        {status === "waiting" ? (
          <div className="flex gap-2">
            <Button
              className={blackButton}
              disabled={isDisabled}
              onClick={() => onAnswer(false)}
            >
              Confirm
            </Button>
            <Button
              className={grayButton}
              disabled={isDisabled}
              onClick={() => onAnswer(true)}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <p className="text-xs text-darkgray">{STATUS_LINE[status] || status}</p>
        )}
      </div>
    </div>
  );
}
